import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AuthService from '../../../lib/authService';
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import LeftPanel from './LeftPanel';
import { pushCleverTapEvent } from '../pushCleverTapEvent';

const schema = yup.object({
  email: yup.string().email('Invalid email format').required('Email is required'),
  password: yup.string().required('Password is required')
});

const Login = ({ onSignUpClick, onForgotPasswordClick, onClose, onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const handleCaptchaSubmit = useCallback(
    async (data) => {
      if (!executeRecaptcha) {
        setError('ReCAPTCHA not yet available. Please try again in a few seconds.');
        return;
      }
  
      try {
        setLoading(true);
        setError('');
        // 🔹 Step 1: Get captcha token
        const gReCaptchaToken = await executeRecaptcha("login");
  
        // 🔹 Step 2: Login API call
        const response = await AuthService.loginDetails(
          data.email,
          data.password,
          gReCaptchaToken
        );
        pushCleverTapEvent("user_login",{email: data.email,'PageName': 'Login'});
        window.gtag('event', 'login');

        const accessToken = response?.result?.access_token;
  
        if (accessToken) {
          localStorage.setItem('credlix_auth_token', accessToken);
  
          if (rememberMe) {
            localStorage.setItem('credlix_remember_email', data.email);
          } else {
            localStorage.removeItem('credlix_remember_email');
          }
          const profileResponse = await AuthService.getUserProfile(accessToken);

          onLoginSuccess &&
            onLoginSuccess({
              email: data.email,
              token: accessToken,
              profile: profileResponse,
              rememberMe,
            });
        } else {
          throw new Error('Invalid login response — token missing.');
        }
      } catch (err) {
        console.error('Login error:', err);
        const message =
          err?.message ||
          err?.data?.message ||
          'Invalid email or password. Please try again.';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [executeRecaptcha, rememberMe, onLoginSuccess]
  );
  

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-[20px] shadow-xl w-full max-w-3xl flex overflow-hidden">
        <LeftPanel />

        {/* Right Panel */}
        <div className="w-[55%] sm:w-full sm:p-3 p-4 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-bold text-black mb-xsp">Welcome Back!</h3>
            <p className="text-[#979797] text-xs mb-3">Manage your supply chain financing with ease.</p>

            <form onSubmit={handleSubmit(handleCaptchaSubmit)}>
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-[#3C3C3C] mb-1">
                  Enter Email
                </label>
                <input
                  type="email"
                  {...register('email')}
                  placeholder="Enter registered Email"
                  className="w-full px-1 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all sm:text-sm"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              {/* Password */}
              <div className='mt-3'>
                <label className="block text-sm font-semibold text-[#3C3C3C] mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    placeholder="Enter your password"
                    className="w-full px-1 py-1 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
                )}
              </div>

              {/* Remember + Forgot */}
              <div className="flex items-center justify-between mt-1">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-3 w-3 text-brand focus:ring-brand border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-1 text-sm text-gray-600">
                    Remember me
                  </label>
                </div>
                <button
                  type="button"
                  onClick={onForgotPasswordClick}
                  className="text-sm text-[#6F6F6F] hover:text-brand transition-colors"
                >
                  Forgot Password?
                </button>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand text-sm text-white py-2 sm:py-1 px-1 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all mt-6 sm:mt-3"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Signing In...
                  </div>
                ) : (
                  'Continue'
                )}
              </button>
            </form>
            {error && (
              <div className="text-red-700 rounded-lg my-1 text-sm">
                {error}
              </div>
            )}
            <div className="mt-4 sm:mt-2 text-center">
              <span className="text-[#979797] text-sm">Don't have an account? </span>
              <button
                onClick={onSignUpClick}
                className="text-brand font-medium hover:underline"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
